import { useEffect, useRef, useState } from "react";
import { Volume2, Play, RefreshCcw } from "lucide-react";
import { speak } from "@/lib/tts";
import { PROSODY_ITEMS } from "@/lib/prosodyData";
import { randomFrom } from "@/lib/gameData";

const CHUNK_GAP_MS = 500;

export default function ChunkProsody() {
  const [item, setItem] = useState(() => randomFrom(PROSODY_ITEMS));
  const [activeIdx, setActiveIdx] = useState(-1);
  const [playing, setPlaying] = useState(false);
  const timersRef = useRef([]);

  const clear = () => {
    timersRef.current.forEach((t) => clearTimeout(t));
    timersRef.current = [];
  };

  useEffect(() => () => clear(), []);

  useEffect(() => {
    clear();
    setActiveIdx(-1);
    setPlaying(false);
  }, [item]);

  const playAll = () => {
    clear();
    setPlaying(true);
    // Speak each chunk in sequence, waiting for its estimated duration + gap
    let cursor = 0;
    item.chunks.forEach((chunk, i) => {
      const dwell = Math.max(1100, chunk.length * 55); // rough duration estimate
      const t = setTimeout(() => {
        setActiveIdx(i);
        speak(chunk, { rate: 0.85 });
      }, cursor);
      timersRef.current.push(t);
      cursor += dwell + CHUNK_GAP_MS;
    });
    const done = setTimeout(() => {
      setActiveIdx(-1);
      setPlaying(false);
    }, cursor);
    timersRef.current.push(done);
  };

  const playChunk = (i) => {
    setActiveIdx(i);
    speak(item.chunks[i], { rate: 0.85 });
    const t = setTimeout(() => setActiveIdx(-1), Math.max(1100, item.chunks[i].length * 55));
    timersRef.current.push(t);
  };

  const nextItem = () => {
    let pick = randomFrom(PROSODY_ITEMS);
    let guard = 5;
    while (pick.id === item.id && guard-- > 0) pick = randomFrom(PROSODY_ITEMS);
    setItem(pick);
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="inline-flex items-center gap-2 bg-purple-50 text-purple-700 px-4 py-2 rounded-full font-bold text-sm">
          Chunk &amp; Prosody
        </div>
        <button
          data-testid="prosody-next"
          onClick={nextItem}
          className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 bg-slate-100 hover:bg-slate-200 font-bold text-sm text-slate-700 transition-colors"
        >
          <RefreshCcw className="w-4 h-4" /> New sentence
        </button>
      </div>

      <div className="bg-slate-50 rounded-3xl p-6 sm:p-8 mb-6">
        <p className="text-sm font-bold uppercase tracking-widest text-slate-500 mb-4">
          Read in chunks &mdash; feel the natural rhythm.
        </p>
        <div className="flex flex-wrap gap-x-2 gap-y-3 text-xl sm:text-2xl leading-relaxed">
          {item.chunks.map((chunk, i) => {
            const isActive = activeIdx === i;
            return (
              <button
                key={`${item.id}-${i}`}
                data-testid={`prosody-chunk-${i}`}
                onClick={() => playChunk(i)}
                className={`rounded-xl px-3 py-1.5 font-medium transition-all
                  ${isActive
                    ? "bg-purple-500 text-white shadow-md scale-105"
                    : "bg-white text-slate-800 border-2 border-slate-200 hover:border-purple-300 hover:text-purple-700"
                  }
                `}
              >
                {chunk}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex flex-wrap justify-center gap-3">
        <button
          data-testid="prosody-play-all"
          onClick={playAll}
          disabled={playing}
          className="inline-flex items-center gap-2 rounded-full px-6 py-3 bg-purple-500 hover:bg-purple-600 text-white font-bold shadow-md hover:shadow-lg disabled:opacity-50 transition-all"
        >
          <Play className="w-5 h-5" fill="currentColor" /> Read with expression
        </button>
        <button
          data-testid="prosody-play-plain"
          onClick={() => {
            clear();
            speak(item.chunks.join(" "), { rate: 0.95 });
          }}
          className="inline-flex items-center gap-2 rounded-full px-5 py-3 bg-white border-2 border-slate-200 text-slate-800 font-bold hover:border-purple-400 transition-colors"
        >
          <Volume2 className="w-5 h-5" /> Hear as one sentence
        </button>
      </div>
    </div>
  );
}
